import { FC, useMemo } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { PreviewBadge } from '@/components/ui/preview-badge';

export type CollectionTypeOption = 'unstructured' | 'structured';

interface CollectionTypeSelectorProps {
  value: CollectionTypeOption;
  onChange: (value: CollectionTypeOption) => void;
  disabled?: boolean;
  showUnstructured?: boolean;
}

const COLLECTION_TYPE_LABELS: Record<CollectionTypeOption, string> = {
  unstructured: 'Unstructured data (Qdrant)',
  structured: 'Structured data (analytics)',
};

export const CollectionTypeSelector: FC<CollectionTypeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  showUnstructured = true,
}) => {
  const visibleOptions = useMemo(
    () =>
      Object.entries(COLLECTION_TYPE_LABELS).filter(
        ([key]) => key !== 'unstructured' || showUnstructured,
      ),
    [showUnstructured],
  );

  return (
    <div className='space-y-3'>
      <p className='text-sm font-medium text-foreground'>Collection type</p>
      <p className='text-sm text-muted-foreground'>
        Choose how you plan to store and process the data in this collection.
      </p>
      <RadioGroup
        value={value}
        onValueChange={(next) => onChange(next as CollectionTypeOption)}
        className='space-y-2'
        disabled={disabled}
      >
        {visibleOptions.map(([key, label]) => {
          const option = key as CollectionTypeOption;
          return (
            <div
              key={option}
              className='flex items-center gap-3 rounded-md border border-border px-3 py-2 hover:border-primary/60 transition-colors'
            >
              <RadioGroupItem
                value={option}
                id={`collection-type-${option}`}
                disabled={disabled}
              />
              <Label
                htmlFor={`collection-type-${option}`}
                className='text-sm font-medium cursor-pointer flex-1 flex items-center gap-2'
              >
                {label}
                {option === 'structured' && <PreviewBadge />}
              </Label>
            </div>
          );
        })}
      </RadioGroup>
    </div>
  );
};
